import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Sentinel error message thrown by `getAuthHeaders` when no Supabase session
 * is available. The `withAuth` wrapper looks for this exact string (and a few
 * server-side 401 variants) to trigger a global "session expired" handler.
 */
export const SESSION_EXPIRED_MESSAGE =
  "Your session expired. Please sign in again.";

/**
 * Returns headers with the current user's Supabase access token attached
 * as `Authorization: Bearer ...`. Use when calling any server function
 * protected by `requireSupabaseAuth`.
 *
 * Throws a clean, user-friendly error if no session is available so the
 * caller can show a "Please sign in again" toast instead of a cryptic
 * 401 from the middleware.
 */
export async function getAuthHeaders(): Promise<{ Authorization: string }> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }
  return { Authorization: `Bearer ${token}` };
}

// Module-level debounce so multiple failing in-flight calls only fire one
// toast + one redirect.
let redirecting = false;

/**
 * Reset the session-expired debounce. Call this after a successful login so
 * that if the user's session expires again later in the same tab, the global
 * handler fires (toast + redirect) instead of being silently suppressed.
 */
export function resetSessionExpiredGuard(): void {
  redirecting = false;
}

const AUTH_ROUTES = ["/login", "/signup", "/reset-password", "/accept-invite"];

function isAuthErrorMessage(message: string): boolean {
  if (message === SESSION_EXPIRED_MESSAGE) return true;
  // Server middleware (`requireSupabaseAuth`) throws messages like:
  //   "Unauthorized: No authorization header provided"
  //   "Unauthorized: Invalid token"
  //   "Unauthorized: ..."
  if (message.startsWith("Unauthorized")) return true;
  if (message.includes("No authorization header")) return true;
  if (message.includes("Invalid token")) return true;
  return false;
}

async function handleSessionExpired(): Promise<void> {
  if (redirecting) return;
  redirecting = true;

  if (typeof window === "undefined") return;

  const path = window.location.pathname;
  // Don't toast/redirect if already on an auth page — avoid loops.
  if (AUTH_ROUTES.some((route) => path.startsWith(route))) {
    redirecting = false;
    return;
  }

  toast.error(SESSION_EXPIRED_MESSAGE);

  try {
    await supabase.auth.signOut();
  } catch {
    // Ignore — local session may already be gone.
  }

  window.location.assign("/login");
}

/**
 * Wraps an authenticated TanStack server function so that callers don't have
 * to manually thread the access token. Returns a function with the same
 * `data` argument, but headers are injected automatically.
 *
 * If the user's session is missing or rejected by the server, this wrapper
 * intercepts the error globally: it shows a toast, signs the user out, and
 * redirects to `/login`. Other errors are rethrown so existing per-call
 * try/catch blocks can still surface specific failures.
 *
 * We intentionally type the wrapped function loosely (`any`): TanStack's
 * generated `RequiredFetcher` type makes `data` mandatory or omitted based on
 * whether the server function has an `inputValidator`, and threading that
 * conditional through a generic helper produces unhelpful TS errors. Runtime
 * validation still happens server-side via Zod.
 *
 * Usage:
 *   const { staff } = await withAuth(listStaff)();
 *   await withAuth(inviteCashier)({ data: { email, locationIds } });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withAuth<TFn extends (args: any) => Promise<any>>(fn: TFn) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return async (args?: { data?: any }): Promise<Awaited<ReturnType<TFn>>> => {
    try {
      const headers = await getAuthHeaders();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return await fn({ data: args?.data, headers } as any);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error ?? "");
      if (isAuthErrorMessage(message)) {
        // Fire-and-forget; we still rethrow so the caller's try/catch settles.
        void handleSessionExpired();
      }
      throw error;
    }
  };
}
